import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import SignaturePad from '@/components/signature-pad';
import type { SignableFieldData } from './signable-field';

interface FieldInputModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  field: SignableFieldData | null;
  /** Called when user confirms the value */
  onConfirm: (fieldId: string, value: string, signatureImage?: string | null) => void;
  signerName?: string;
  signerEmail?: string;
  savedSignatureImage?: string | null;
}

const FieldInputModal: React.FC<FieldInputModalProps> = ({
  open,
  onOpenChange,
  field,
  onConfirm,
  signerName = '',
  signerEmail = '',
  savedSignatureImage = null,
}) => {
  const { t } = useTranslation(['documents']);
  const [textValue, setTextValue] = useState('');
  const [signatureData, setSignatureData] = useState<string | null>(null);

  // Pre-fill based on field type
  useEffect(() => {
    if (!field || !open) return;
    setSignatureData(null);

    switch (field.type) {
      case 'name':
      case 'fullname':
        setTextValue(signerName);
        break;
      case 'email':
        setTextValue(signerEmail);
        break;
      case 'date':
        setTextValue(new Date().toLocaleDateString());
        break;
      case 'initial':
        setTextValue(
          signerName
            .split(' ')
            .filter(Boolean)
            .map((n) => n[0]?.toUpperCase() || '')
            .join('')
        );
        break;
      case 'signature':
        setTextValue('');
        break;
      default:
        setTextValue('');
    }
  }, [field, open, signerName, signerEmail]);

  const handleConfirm = () => {
    if (!field) return;

    if (field.type === 'signature') {
      // For signature, signatureData is the image and textValue is the fallback name
      onConfirm(field.id, textValue || signerName, signatureData);
    } else {
      onConfirm(field.id, textValue, null);
    }
    onOpenChange(false);
  };

  const isSignatureType = field?.type === 'signature';
  const canConfirm = isSignatureType ? Boolean(signatureData) : Boolean(textValue.trim());

  const getTitle = () => {
    if (!field) return '';
    const typeKey: Record<string, string> = {
      signature: 'field_input_signature',
      initial: 'field_input_initial',
      name: 'field_input_name',
      email: 'field_input_email',
      date: 'field_input_date',
      city: 'field_input_city',
      position: 'field_input_position',
      province: 'field_input_province',
      state: 'field_input_state',
    };
    return t(`documents:${typeKey[field.type] || 'field_input_generic'}`);
  };

  if (!field) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={isSignatureType ? 'sm:max-w-[500px]' : 'sm:max-w-[400px]'} onOpenAutoFocus={(e) => { if (!isSignatureType) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
        </DialogHeader>

        {isSignatureType ? (
          <SignaturePad
            signerName={signerName}
            onSignatureChange={setSignatureData}
            savedSignatureImage={savedSignatureImage}
          />
        ) : (
          <div className="space-y-2">
            <Label htmlFor="field-value">{getTitle()}</Label>
            <Input
              id="field-value"
              value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
              placeholder={t('documents:field_input_placeholder')}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canConfirm) handleConfirm();
              }}
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('documents:field_input_cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={!canConfirm} className="bg-green-600 hover:bg-green-700 text-white">
            {t('documents:field_input_confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FieldInputModal;
